import Logger from 'utils/logger';
import {
  decryptStorageValue,
  encryptStorageValue,
  isEncryptedStorageValue,
  isSensitiveStorageKey
} from './tokenEncryption';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Window = window as any;

const serializeValue = <T>(value: T): string => (typeof value === 'object' ? JSON.stringify(value) : `${value}`);

export const LocalStorage = {
  set<T = string | boolean>(key: string, value: T): void {
    try {
      if (value === null || value === undefined) {
        Window.localStorage?.removeItem(key);
        return;
      }

      const serialized = serializeValue(value);
      const valueToStore = isSensitiveStorageKey(key) ? encryptStorageValue(serialized) : serialized;
      Window.localStorage?.setItem(key, valueToStore);
    } catch (error) {
      Logger.logException(error);
    }
  },
  remove<T = string | boolean>(key: string): T {
    try {
      return Window.localStorage?.removeItem(key) as T;
    } catch (error) {
      Logger.logException(error);
      return null;
    }
  },
  get<T = string | boolean>(key: string): T {
    try {
      const rawValue = Window.localStorage?.getItem(key);
      if (rawValue === null || rawValue === undefined) {
        return null;
      }

      if (isSensitiveStorageKey(key)) {
        const decrypted = decryptStorageValue(rawValue);
        if (decrypted === null) {
          return null;
        }

        // Re-encrypt legacy plaintext tokens on read so storage stays encrypted.
        if (!isEncryptedStorageValue(rawValue)) {
          Window.localStorage?.setItem(key, encryptStorageValue(decrypted));
        }

        return tryParse(decrypted) as T;
      }

      return tryParse(rawValue) as T;
    } catch (error) {
      Logger.logException(error);
      return null;
    }
  },

  clear() {
    try {
      return Window.localStorage?.clear();
    } catch (error) {
      Logger.logException(error);
      return null;
    }
  }
};

function tryParse(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
export const SessionStore = {
  set<T = string | boolean>(key: string, value: T): void {
    try {
      Window.sessionStorage?.setItem(key, value.toString());
    } catch (error) {
      Logger.logException(error);
    }
  },
  get<T = string | boolean>(key: string): T {
    try {
      return Window.sessionStorage?.getItem(key) as T;
    } catch (error) {
      Logger.logException(error);
      return null;
    }
  }
};
